// `search.page.tpl.html:60` — the criteria builder's first section: one row per field of
// expertise, each with a field, a position, a years range and a "when".
//
// ⚠️ WHAT IS PORTED IS THE GRID, NOT THE TITLE AUTOCOMPLETE. The position cell has two forms,
// switched on the row's `field`: an ordinary select of the paths for that field, and — for the
// `OTHER` field — a `tags-input` over a live job-title autocomplete with category
// recommendations beneath it. That second form is several hundred lines and its own API; it
// lands next. The select form is what every reference capture of this screen shows.
//
// Two upstream details the grid itself carries:
//
//   - the FIELD column disappears entirely when `extendedTitleClassFeatureEnabled` is on,
//     because every row is then implicitly "other fields". It is a whole `criteria-cell`, so
//     the grid is four columns wide or three depending on a flag.
//   - the operator word ("AND"/"OR") is printed BETWEEN rows — inside the field cell of every
//     row except the last — rather than once for the section.
import { emptyFieldOfExpertiseRow } from './criteriaLoad.ts'
import { fieldOfExpertiseCriteriaValid, otherPositionsValid } from './criteriaValidity.ts'
import { useMemo, type ReactElement } from 'react'
import { WooRadio } from '../common/WooRadio.tsx'
import { TagsInput, type Tag } from '../common/TagsInput.tsx'
import { TitleSuggestionPanel } from './TitleSuggestionPanel.tsx'
import { CategoryTip, TitleRecommendations, showCategoryTip } from './TitleRecommendations.tsx'
import type { TitleRecommendation } from '@woo-ext/api'

import { tagsPlaceholder } from './SkillSetSection.tsx'
import {
  LAST_USED_OPTIONS,
  LOGICAL_OPERATOR_OPTIONS,
  LOGICAL_OPERATOR_CLAUSE,
  MAX_YEARS_OPTIONS,
  MIN_YEARS_OPTIONS,
  type LastUsedPeriod,
  type LogicalOperator,
} from './criteriaOptions.ts'

/** `ITempFieldOfExpertise`, minus the title-recommendation state the autocomplete owns. */
export interface FieldOfExpertiseRow {
  field?: string
  position?: string
  /**
   * The free-title list, used INSTEAD of `position` when the field is the "Other fields"
   * sentinel — see the position cell.
   */
  otherPositions?: Tag[]
  minYears?: number | null
  maxYears?: number | null
  when?: LastUsedPeriod
}

export interface FieldOption {
  /** The enum VALUE the criteria store, which is not the display name. */
  enum: string
  name: string
}

export interface FieldOfExpertiseSectionProps {
  /** `ctrl.getJobTitles($query, foe.otherPositions)` — the row's own titles are excluded. */
  loadTitles?: (index: number, query: string) => Promise<readonly Tag[]>
  rows: readonly FieldOfExpertiseRow[]
  /** `ctrl.fieldTypes`, rendered `| orderBy:'name'`. */
  fieldTypes: readonly FieldOption[]
  /** `ctrl.pathPerField` — the positions available for each field. */
  pathPerField?: Readonly<Record<string, ReadonlyArray<{ value: string; display: string }>>>
  /** `ctrl.otherFieldsFieldName` — the sentinel value for "Other fields". */
  otherFieldsFieldName?: string
  /** `ctrl.canViewGenericSearch()` — gates the divider and the "Other fields" option. */
  canViewGenericSearch?: boolean
  /**
   * `extendedTitleClassFeatureEnabled` — removes the field column entirely.
   *
   * ⚠️ AND MAKES EVERY ROW AN "OTHER" ROW. The template says so in a comment beside the
   * switch: "if flag is enabled then all fields are OTHER". So turning it on does not merely
   * hide a column — it changes which control the POSITION cell renders, from a select of
   * `pathPerField` to a free-title tags input.
   */
  extendedTitleClass?: boolean
  /**
   * `extendedTitleClassUXFeatureEnabled` = `extendedTitleClassFeatureEnabled && UX flag` —
   * ⚠️ THE SECOND FLAG IS ANDED WITH THE FIRST, not read on its own. It swaps the tags input
   * for a second one carrying `tags-input-extended-title-class-ux-recommendations`; upstream
   * duplicates the whole element rather than switching its template, with a HACK comment
   * saying tags-input does not play nicely with dynamic templates.
   */
  extendedTitleClassUX?: boolean
  /** Suggestions for the free-title input (`getJobTitles`). */
  titleSuggestions?: readonly Tag[]
  /** `foe.titleRecommendations`, per row — `getTitleRecommendationsForTitlesV2`. */
  recommendations?: Readonly<Record<number, readonly TitleRecommendation[]>>
  recommendationsLoading?: boolean
  onSelectRecommendation?: (index: number, title: string) => void
  onTitleQueryChange?: (query: string) => void
  /** `fieldOfExpertiseOperator` — prints between rows when set. */
  operator?: LogicalOperator | null
  onChange?: (index: number, row: FieldOfExpertiseRow) => void
  onRemove?: (index: number) => void
  onAdd?: () => void
}

export function FieldOfExpertiseSection({
  rows,
  fieldTypes,
  pathPerField = {},
  otherFieldsFieldName = 'OTHER',
  canViewGenericSearch = false,
  extendedTitleClass = false,
  extendedTitleClassUX = false,
  titleSuggestions = [],
  recommendations = {},
  recommendationsLoading = false,
  onSelectRecommendation,
  onTitleQueryChange,
  operator,
  onChange,
  onRemove,
  onAdd,
  loadTitles,
}: FieldOfExpertiseSectionProps): ReactElement {
  // `| orderBy:'name'` — by the DISPLAY name, not the enum value they are stored as.
  const sortedFields = [...fieldTypes].sort((a, b) => a.name.localeCompare(b.name))
  // `validateFieldOfExpertiseCriteria` — see `criteriaValidity.ts`. The empty row is rebuilt
  // here rather than passed in, because "empty on load" is the section's own notion.
  // ⚠️ THE FIELD IS PART OF THE SIGNATURE HERE AND THE TAG COUNTS ARE TOO, because this
  // section's `onFOEChanged` IS the field select's `ng-change` — unlike the skill and keyword
  // sections, whose selects do not revalidate. Years and "when" are left out: they reach
  // `onFOEPeriodChanged`, which does not validate.
  const validitySignature = rows
    .map((row) => `${row.field ?? ''}:${String(row.otherPositions?.length ?? 0)}`)
    .join('|')
  const sectionValid = useMemo(
    () =>
      fieldOfExpertiseCriteriaValid({
        rows,
        otherFieldsFieldName,
        extendedTitleClass,
        emptyRow: emptyFieldOfExpertiseRow(otherFieldsFieldName, extendedTitleClass),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the signature IS the dependency.
    [validitySignature, otherFieldsFieldName, extendedTitleClass],
  )

  return (
    <div className="field-of-expertise-criteria criteria-table">
      <div className="criteria-row">
        {extendedTitleClass ? null : (
          <div className="criteria-cell-label">
            <label className="bold-soft">Field</label>
          </div>
        )}
        <div className="criteria-cell-label">
          <label className="bold-soft">Position</label>
        </div>
        <div className="criteria-cell-label">
          <label className="bold-soft">Years of experience</label>
        </div>
        <div className="criteria-cell-label">
          <label className="bold-soft">When</label>
        </div>
      </div>

      {rows.map((row, index) => (
        // eslint-disable-next-line react/no-array-index-key -- rows have no id upstream either;
        // they are positional, and `removeFOE` is by index.
        <div className="criteria-row sub-section" key={index}>
          {extendedTitleClass ? null : (
            <div className="criteria-cell">
              <div className="select" style={{ width: '200px' }}>
                <select
                  name="field"
                  className="form-control input"
                  value={row.field ?? ''}
                  onChange={(event) => onChange?.(index, { ...row, field: event.target.value })}
                >
                  <option className="color-graylight" value="" disabled>
                    Please select...
                  </option>
                  {sortedFields.map((type) => (
                    <option key={type.enum} value={type.enum}>
                      {type.name}
                    </option>
                  ))}
                  {canViewGenericSearch ? (
                    <>
                      {/* A disabled rule, drawn with box characters — it is an OPTION upstream,
                          not an `<optgroup>`, and it sits inside the same list. */}
                      <option disabled>────────────</option>
                      <option value={otherFieldsFieldName}>Other fields</option>
                    </>
                  ) : null}
                </select>
              </div>
              {operator !== undefined && operator !== null && row.field !== undefined && index < rows.length - 1 ? (
                <div className="mT10">
                  <label className="bold-soft logical-operator-clauses">{LOGICAL_OPERATOR_CLAUSE[operator]}</label>
                </div>
              ) : null}
            </div>
          )}

          {/* ⚠️ AN `ng-switch` ON THE FIELD, NOT A SECOND COLUMN. The "Other fields" sentinel
              replaces the position SELECT with a free-title TAGS INPUT — a different control
              against a different criteria key (`otherPositions`, not `position`).
              
              ⚠️ AND THE FLAG ALONE IS ENOUGH, without inspecting the field. Upstream says so
              where it explains why it did NOT use an `ng-if` here: "if flag is enabled than all
              fields are OTHER and therefore ng-switch will not load the position select into
              the DOM". Testing only the sentinel looks stricter and is wrong on a tenant whose
              bootstrap has not supplied `otherFieldsFieldName` — which is every tenant until
              plan §4.1 deploys, so the strict reading renders the wrong control on the only
              tenant we can test against. */}
          <div className="criteria-cell">
            {extendedTitleClass || (row.field !== undefined && row.field === otherFieldsFieldName) ? (
              <div
                className={`other-positions-wrap ${
                  extendedTitleClass ? 'foe-extended-titles-enabled' : 'foe-extended-titles-disabled'
                }`}
              >
                <TagsInput
                  className={`tags-input-default-width or-in-between${
                    extendedTitleClassUX ? ' tags-input-extended-title-class-ux-recommendations' : ''
                  }`}
                  name={`otherPositions${String(index)}`}
                  placeholder={tagsPlaceholder(row.otherPositions, 'title', 'Add position (title)')}
                  tags={row.otherPositions ?? []}
                  suggestions={titleSuggestions}
                  onQueryChange={onTitleQueryChange}
                  {...(loadTitles === undefined
                    ? {}
                    : { loadSuggestions: (query: string) => loadTitles(index, query) })}
                  onAdd={(tag) => onChange?.(index, { ...row, otherPositions: [...(row.otherPositions ?? []), tag] })}
                  onRemove={(at) =>
                    onChange?.(index, {
                      ...row,
                      otherPositions: (row.otherPositions ?? []).filter((_, i) => i !== at),
                    })
                  }
                  renderSuggestions={TitleSuggestionPanel}
                  // `other-positions.title-class-ux.ng-tags.tpl.html` when the UX flag is on,
                  // `other-positions.ng-tags.tpl.html` when it is not — upstream renders two
                  // `tags-input` elements and picks between them with `ng-if`.
                  tagTemplate={extendedTitleClassUX ? 'position-ux' : 'position'}
                />
                {/* ⚠️ THE TIP COMES BEFORE THE BLOCK, and it is the one piece of this group
                    that neither builder had. `search.page.tpl.html:184`. */}
                {extendedTitleClassUX && showCategoryTip(row.otherPositions, recommendations[index] ?? [])
                  ? <CategoryTip />
                  : null}
                {/* ⚠️ THE SUGGESTIONS BLOCK RENDERS AS SOON AS THE ROW HAS A TITLE, AND IT HAS
                    AN EMPTY STATE. `ng-if="extendedTitleClassUXFeatureEnabled &&
                    otherPositions.length > 0"` (`search.page.tpl.html:190`) — the three
                    branches inside it are loading, "No recommended titles" and the two
                    sections. This port drew none of them, so the block was simply absent
                    where production shows it, including when there is nothing to recommend. */}
                {extendedTitleClassUX && (row.otherPositions?.length ?? 0) > 0 ? (
                  <TitleRecommendations
                    rows={recommendations[index] ?? []}
                    loading={recommendationsLoading}
                    onSelect={(title) => onSelectRecommendation?.(index, title)}
                  />
                ) : null}
                {/* ⚠️ A SECOND AND-LABEL, FOR THE EXTENDED PATH, AND IT IS A LITERAL.
                    `search.page.tpl.html:249` renders `AND` — not
                    `logicalOperatorClauseText[operator]`, which is what the non-extended label
                    twenty lines above it uses. Its condition does not mention `foe.field`
                    either, and it could not: with the flag on there is no field select, so the
                    label keyed on one never appeared and the rows ran together. */}
                {extendedTitleClass && operator !== undefined && operator !== null && index < rows.length - 1 ? (
                  <div className="mT10">
                    <label className="bold-soft logical-operator-clauses">AND</label>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="select" style={{ width: '240px' }}>
                <select
                  name="position"
                  className="form-control input"
                  value={row.position ?? ''}
                  onChange={(event) => onChange?.(index, { ...row, position: event.target.value })}
                >
                  {/* An empty value that is NOT disabled — "Any position" is a real choice. */}
                  <option className="color-graylight" value="">
                    Any position
                  </option>
                  {(pathPerField[row.field ?? ''] ?? []).map((path) => (
                    <option key={path.value} value={path.value}>
                      {path.display}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {/* `search.page.tpl.html:86` — a row with no field, once the section is invalid.
                `search.page.tpl.html:88` — an "Other fields" row with no titles, which is
                checked per ROW rather than against the section's validity. Neither label was
                ported, so an incomplete row looked finished and Save did nothing: production
                blocks the save on exactly these. */}
            {(row.field === undefined || row.field === '') && !sectionValid ? (
              <label className="control-label has-error mB10">Please select a field</label>
            ) : null}
            {!otherPositionsValid(row, otherFieldsFieldName) && !(extendedTitleClass === true && rows.length === 1) ? (
              <label className="control-label has-error mB10">Please select positions</label>
            ) : null}
          </div>

          <div className="criteria-cell">
            <div className="yoe-dropdown">
              <div className="select min-years-dropdown">
                <select
                  name="minYoe"
                  className="form-control input"
                  value={row.minYears ?? ''}
                  onChange={(event) =>
                    onChange?.(index, {
                      ...row,
                      minYears: event.target.value === '' ? null : Number(event.target.value),
                    })
                  }
                >
                  {MIN_YEARS_OPTIONS.map((option) => (
                    <option key={option.display} value={option.value ?? ''}>
                      {option.display}
                    </option>
                  ))}
                </select>
              </div>
              <div className="select max-years-dropdown">
                <select
                  name="maxYoe"
                  className="form-control input"
                  value={row.maxYears ?? ''}
                  onChange={(event) =>
                    onChange?.(index, {
                      ...row,
                      maxYears: event.target.value === '' ? null : Number(event.target.value),
                    })
                  }
                >
                  {MAX_YEARS_OPTIONS.map((option) => (
                    <option key={option.display} value={option.value ?? ''}>
                      {option.display}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="criteria-cell">
            <div className="select last-period-dropdown">
              <select
                name="when"
                className="form-control input"
                value={row.when ?? ''}
                onChange={(event) =>
                  onChange?.(index, { ...row, when: Number(event.target.value) as LastUsedPeriod })
                }
              >
                {LAST_USED_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.display}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            className="remove-criteria-container criteria-cell"
            role="button"
            aria-label="Remove"
            tabIndex={0}
            onClick={() => onRemove?.(index)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return
              event.preventDefault()
              onRemove?.(index)
            }}
          >
            {/* A stacked FA icon: a filled circle behind a thin ×. Both are needed — the × on
                its own has no disc. */}
            <div className="fa-stack fa-1x icons-container">
              <i className="fas fa-circle fa-stack-1x x-icon-container fa-lg" />
              <i className="fal fa-times fa-stack-1x cursor-pointer fa-xs" />
            </div>
          </div>
        </div>
      ))}

      {/* ⚠️ THE PLACEHOLDER CELLS ARE LOAD-BEARING. The add button has to land under the last
          column, so the row carries empty `criteria-cell placeholder-cell` divs to push it
          there — and one of them is itself behind the flag, because the grid loses a column
          with the field cell. Dropping them (they contain nothing) moves the button left by
          two columns. */}
      <div className="criteria-row bottom-cta-row sub-section">
        <div className="criteria-cell">
          {rows.length > 1 && !extendedTitleClass ? <label className="bold-soft">Options</label> : null}
        </div>
        <div className="criteria-cell placeholder-cell" />
        {extendedTitleClass ? null : <div className="criteria-cell placeholder-cell" />}
        <div className="add-foe-button criteria-cell">
          <a
            href=""
            className="add-button"
            onClick={(event) => {
              event.preventDefault()
              onAdd?.()
            }}
          >
            <i className="fal fa-plus-circle" />
            <span>&nbsp;Add</span>
          </a>
        </div>
      </div>
    </div>
  )
}

/**
 * The "Match all" / "Match one or more" radios, which sit OUTSIDE the criteria table.
 *
 * ⚠️ AND ONLY WITH MORE THAN ONE ROW. A single field of expertise has nothing to combine, so
 * upstream hides the choice rather than defaulting it — the operator stays null and the
 * validator only demands it once a second row exists.
 */
export function FieldOfExpertiseOperator({
  rows,
  operator,
  extendedTitleClass = false,
  onChange,
}: {
  rows: number
  operator?: LogicalOperator | null
  extendedTitleClass?: boolean
  onChange?: (operator: LogicalOperator) => void
}): ReactElement | null {
  if (rows <= 1 || extendedTitleClass) return null
  return (
    <div className="foe-operator-radio-container">
      {LOGICAL_OPERATOR_OPTIONS.map((option) => (
        <div key={option.value}>
          <WooRadio
            name="fieldOfExpertiseOperator"
            value={String(option.value)}
            checked={operator === option.value}
            onChange={() => onChange?.(option.value)}
          >
            {option.display}
          </WooRadio>
        </div>
      ))}
    </div>
  )
}
